'use client';

import { RefObject, useId, useRef, useState } from 'react';
import { FocusTrap } from 'focus-trap-react';
import { Link } from '@/ui-components/atoms/Link/Link';
import { Modal } from '@/ui-components/molecules/Modal/Modal';
import { Text } from '@/ui-components/atoms/Text/Text';
import styles from './radioLookupModal.module.scss';

export type RadioLookupTab = 'cars' | 'marine-aviation';

export interface RadioLookupModalProps {
    open: boolean;
    onClose: () => void;
    initialTab?: RadioLookupTab;
    onSearchByLicensePlate?: () => void;
    restoreFocusRef?: RefObject<HTMLElement | null>;
}

function CarsTab({ onSearchByLicensePlate }: Pick<RadioLookupModalProps, 'onSearchByLicensePlate'>): React.JSX.Element {
    return (
        <div className={styles.carsPanel}>
            <div className={styles.copyBlock}>
                <p className={styles.copy}>
                    <strong>Most Radios</strong>
                    <br />
                    Tune your radio dial to channel 0 and the radio ID should display. If this doesn&apos;t work, check
                    the menu function on your radio, refer to your owner&apos;s manual for specific instructions.
                </p>
                <p className={styles.copy}>
                    <strong>Other Radios</strong>
                    <br />
                    Check under your radio menu or the subscription tab within your vehicle&apos;s SiriusXM app. For some
                    radios, you may need to press and hold the SAT or ESN button.
                </p>
            </div>
            <div className={styles.footerLinks}>
                <button type="button" className={styles.linkButton} onClick={onSearchByLicensePlate}>
                    Search by License Plate
                </button>
                <Link href="https://www.siriusxm.com/help/find-radio-id" className={styles.textLink}>
                    Get more help for other radios
                </Link>
            </div>
        </div>
    );
}

function MarineAviationTab(): React.JSX.Element {
    return (
        <ul className={styles.bulletList}>
            <li className={styles.bulletItem}>
                For most devices, access your <strong>Diagnostics</strong> or <strong>Settings</strong> menu, and then
                look for your ESN/Radio ID/Device ID to be listed.
            </li>
            <li className={styles.bulletItem}>
                In many cases the Radio ID will be on the bottom of the device, or the packaging that it came with. It
                is likely found <strong>next to the serial number</strong>.
            </li>
            <li className={styles.bulletItem}>
                If your device has audio capability, <strong>tune to channel 0</strong> and your Radio ID will display
                on screen.
            </li>
            <li className={styles.bulletItem}>
                <strong>Contact the radio manufacturer</strong> to help find your Radio ID.
            </li>
        </ul>
    );
}

function RadioLookupModalContent({
    initialTab,
    onSearchByLicensePlate,
    titleId,
    descriptionId,
    titleRef,
}: {
    initialTab: RadioLookupTab;
    onSearchByLicensePlate?: () => void;
    titleId: string;
    descriptionId: string;
    titleRef: RefObject<HTMLElement | null>;
}): React.JSX.Element {
    const [activeTab, setActiveTab] = useState<RadioLookupTab>(initialTab);

    return (
        <>
            <div className={styles.header}>
                <div className={styles.titleGroup}>
                    <Text id={titleId} ref={titleRef} tabIndex={-1} tag="h2" variant="h5" className={styles.title}>
                        How do I find my Radio ID?
                    </Text>
                    <Text id={descriptionId} tag="p" variant="text-medium" className={styles.description}>
                        Here are a few ways to find your Radio ID or ESN - either 8 or 12 characters long.
                    </Text>
                </div>
            </div>

            <div className={styles.body}>
                <div className={styles.tabList} role="tablist" aria-label="Radio lookup categories">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'cars'}
                        className={activeTab === 'cars' ? styles.tabButtonActive : styles.tabButton}
                        onClick={() => setActiveTab('cars')}
                    >
                        Cars
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'marine-aviation'}
                        className={activeTab === 'marine-aviation' ? styles.tabButtonActive : styles.tabButton}
                        onClick={() => setActiveTab('marine-aviation')}
                    >
                        Marine &amp; Aviation
                    </button>
                </div>

                <div className={styles.panel}>
                    {activeTab === 'cars' ? (
                        <CarsTab onSearchByLicensePlate={onSearchByLicensePlate} />
                    ) : (
                        <MarineAviationTab />
                    )}
                </div>
            </div>
        </>
    );
}

export function RadioLookupModal({
    open,
    onClose,
    initialTab = 'cars',
    onSearchByLicensePlate,
    restoreFocusRef,
}: RadioLookupModalProps): React.JSX.Element {
    const titleId = useId();
    const descriptionId = useId();
    const titleRef = useRef<HTMLElement>(null);

    return (
        <FocusTrap
            active={open}
            focusTrapOptions={{
                initialFocus: () => titleRef.current as HTMLElement,
                fallbackFocus: () => titleRef.current as HTMLElement,
                returnFocusOnDeactivate: false,
            }}
        >
            <Modal
                open={open}
                onClose={onClose}
                variant="small"
                centeredLayout
                isOverflowHidden
                className={styles.modalRoot}
                contentClassName={styles.modalContent}
                ariaLabelledBy={titleId}
                ariaDescribedBy={descriptionId}
                initialFocusRef={titleRef}
                restoreFocusRef={restoreFocusRef}
                dataLocalpagename='overlay:radiolookup'
            >
                <RadioLookupModalContent
                    initialTab={initialTab}
                    onSearchByLicensePlate={onSearchByLicensePlate}
                    titleId={titleId}
                    descriptionId={descriptionId}
                    titleRef={titleRef}
                />
            </Modal>
        </FocusTrap>
    );
}
